/* eslint-disable @typescript-eslint/no-explicit-any */
import i18n from 'i18next';
import I18NextLocizeBackend from 'i18next-locize-backend';
import { initReactI18next } from 'react-i18next';

export const fallbackLocale = 'en';

const EXTENSION_I18N_PROJECT_ID = 'similarweb_si_extens';
const SAM_I18N_PROJECT_ID = 'similarweb_si_sam';

export const SAM_I18N_PROJECT_NS = 'translation_sam';

export const initI18n = (i18nURL: string) => {
  const path = `${i18nURL}/i18n/`;
  const env = i18nURL.includes('sandbox') ? 'staging' : 'production';

  return i18n
    .use(initReactI18next)
    .use(I18NextLocizeBackend)
    .init({
      ns: ['translation'],
      defaultNS: 'translation',
      load: 'currentOnly',
      lowerCaseLng: true,
      preload: [fallbackLocale],
      debug: false,
      fallbackLng: fallbackLocale,
      fallbackNS: 'translation',
      returnEmptyString: false,
      parseMissingKeyHandler: (key, def) => def ?? ``,
      backend: {
        loadPath: `${path}{{projectId}}/{{version}}/{{lng}}/{{ns}}`,
        privatePath: `${path}private/{{projectId}}/{{version}}/{{lng}}/{{ns}}`,
        addPath: `${path}missing/{{projectId}}/{{version}}/{{lng}}/{{ns}}`,
        updatePath: `${path}update/{{projectId}}/{{version}}/{{lng}}/{{ns}}`,
        projectId: EXTENSION_I18N_PROJECT_ID,
        referenceLng: fallbackLocale,
        version: env,
      },
      react: { useSuspense: true },
      interpolation: { escapeValue: false },
    });
};

const loadNamespace = async ({ lng, ns, projectId }: { lng: string; ns: string; projectId: string }) => {
  const env = window.i18nURL.includes('sandbox') ? 'staging' : 'production';

  if (!i18n.hasResourceBundle(lng, ns)) {
    try {
      const res = await fetch(`${window.i18nURL}/i18n/${projectId}/${env}/${lng}/translation`);
      const translations = (await res.json()) as Record<string, string>;
      i18n.addResourceBundle(lng, ns, translations);
    } catch (error) {
      console.error('Error loading namespace:', error);
    }
  }
};

export const loadSAMTranslations = async (lng: string) =>
  loadNamespace({ lng, ns: SAM_I18N_PROJECT_NS, projectId: SAM_I18N_PROJECT_ID });

export type TranslateOptions = Omit<Parameters<typeof i18n.t>[1], 'defaultValue'> & {
  defaultValue?: any;
} & Record<string, any>;

export const translate = (key: string, options: TranslateOptions = { defaultValue: key }) => i18n.t(key, options);

/**
 * Adds `.singular` (and `.plural` if 2nd argument is `true`) suffix to given `i18nKey` string
 * based on given `numberOfItems`
 * @param i18nKey
 * @param withPluralSuffix
 */
export function createPluralHelperForI18nKey(i18nKey: string, withPluralSuffix = false) {
  return function getSingularOrPluralKey(numberOfItems: number) {
    if (numberOfItems === 1) {
      return `${i18nKey}.singular`;
    }

    if (withPluralSuffix) {
      return `${i18nKey}.plural`;
    }

    return i18nKey;
  };
}
